import { useState } from 'react';
import { toast } from 'react-toastify';
import BillingContactExternalForm from 'components/billingContacts/BillingContactExternalForm';
import {
  billingContactChanges,
  billingContactFormFrom,
  billingContactName,
  emptyBillingContactForm,
  toBillingContactPayload,
  validateBillingContactForm,
} from 'components/billingContacts/billingContactForm';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import { BILLING_CONTACTS_UPDATED } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyBillingNotificationsCard';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import {
  readBillingContactError,
  useInviteOnJobbiz,
} from './billingContactModal';

// Form field (as validateBillingContactForm names it) → the api fields it sends.
const FORM_FIELDS_OF_CHANGE = {
  email: ['email'],
  firstName: ['firstname'],
  lastName: ['lastname'],
  phone: ['phone'],
  notifications: ['invoices', 'reminders'],
};

/**
 * A billing contact without an account: mockup 8.5 to add one (no `contact`,
 * what was typed in the search as `initialValues`, `onBack` to return to it),
 * 8.6 to edit one. The edit sends only the fields that changed, and deletes
 * after a confirmation shown in place of the form.
 */
const BillingExternalView = ({
  idCompany,
  contact,
  initialValues,
  title,
  onBack,
}) => {
  const { toggle } = useModal();
  const { language, setModalDataReturn } = UseContext();
  const inviteOnJobbiz = useInviteOnJobbiz();
  const text = (key) => language.getValue(`companyBillingContacts.${key}`, '');
  const isEdit = Boolean(contact);
  const [initialForm] = useState(() =>
    isEdit
      ? billingContactFormFrom(contact)
      : emptyBillingContactForm(initialValues),
  );
  const [form, setForm] = useState(initialForm);
  // Keys under companyBillingContacts.errors, by field.
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const changes = isEdit ? billingContactChanges(initialForm, form) : null;
  const canSave = !isSending && (!isEdit || Object.keys(changes).length > 0);

  const changeField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({
      ...previous,
      [field === 'invoices' || field === 'reminders' ? 'notifications' : field]:
        undefined,
    }));
  };

  const finish = (toastKey) => {
    setIsSending(false);
    toast.success(text(`toasts.${toastKey}`));
    setModalDataReturn({ type: BILLING_CONTACTS_UPDATED });
    toggle();
  };

  const fail = (failure) => {
    const refusal = readBillingContactError(failure);
    if (refusal.kind === 'canceled') return;
    setIsSending(false);
    if (refusal.kind === 'field') {
      setIsConfirmingDelete(false);
      setErrors({ [refusal.field]: refusal.key });
      return;
    }
    toast.error(text(`errors.${refusal.key}`));
    if (refusal.kind === 'gone') {
      setModalDataReturn({ type: BILLING_CONTACTS_UPDATED });
      toggle();
    }
  };

  const save = () => {
    if (!canSave) return;
    // An edit only checks what it sends, as the api does: a contact copied
    // without a name can still have its boxes changed.
    const clientErrors = Object.fromEntries(
      Object.entries(validateBillingContactForm(form)).filter(
        ([field]) =>
          !isEdit ||
          FORM_FIELDS_OF_CHANGE[field].some((apiField) => apiField in changes),
      ),
    );
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    setErrors({});
    setIsSending(true);
    if (isEdit) {
      ApiDatabase.putCompanyBillingContact(
        { idCompany, contactId: contact.id, ...changes },
        () => finish('saved'),
        fail,
      );
      return;
    }
    ApiDatabase.postCompanyBillingContact(
      { idCompany, ...toBillingContactPayload(form) },
      () => finish('added'),
      fail,
    );
  };

  const deleteContact = () => {
    setIsSending(true);
    ApiDatabase.deleteCompanyBillingContact(
      { idCompany, contactId: contact.id },
      () => finish('deleted'),
      fail,
    );
  };

  const errorTexts = Object.fromEntries(
    Object.entries(errors)
      .filter(([, key]) => key)
      .map(([field, key]) => [field, text(`errors.${key}`)]),
  );

  if (isConfirmingDelete) {
    const name = billingContactName(contact) || contact.email;
    return (
      <>
        <Modalheader hide={toggle} hideShow={!isSending}>
          {text('external.deleteTitle').replace('{name}', () => name)}
        </Modalheader>
        <ModalBody>
          <p className={'text-sm text-gray-700'}>
            {text('external.deleteConfirmation')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            size={'LG'}
            color={isSending ? 'DISABLED' : 'SECONDARY'}
            disabled={isSending}
            onClick={() => setIsConfirmingDelete(false)}
          >
            {text('actions.cancel')}
          </Button>
          <Button
            size={'LG'}
            color={isSending ? 'DISABLED' : 'PRIMARY'}
            disabled={isSending}
            onClick={deleteContact}
          >
            {isSending
              ? text('external.deleting')
              : text('external.confirmDelete')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isSending}>
        {title}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <p className={'text-sm text-gray-500'}>
            {text('picker.addExternalHint')}
          </p>
          <BillingContactExternalForm
            form={form}
            errors={errorTexts}
            onChange={changeField}
            isDisabled={isSending}
          />
          {isEdit ? (
            <button
              type={'button'}
              disabled={isSending}
              onClick={() => setIsConfirmingDelete(true)}
              className={
                'self-start text-sm text-red-600 underline underline-offset-4 disabled:text-gray-400'
              }
            >
              {text('external.delete')}
            </button>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type={'button'}
          disabled={isSending}
          onClick={() => inviteOnJobbiz(idCompany, form)}
          className={
            'mr-auto text-sm text-gray-900 underline decoration-dotted underline-offset-4 disabled:text-gray-400'
          }
        >
          {text('external.invite')}
        </button>
        {onBack ? (
          <Button
            size={'LG'}
            color={isSending ? 'DISABLED' : 'SECONDARY'}
            disabled={isSending}
            onClick={onBack}
          >
            {text('actions.back')}
          </Button>
        ) : null}
        <Button
          size={'LG'}
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {text('actions.cancel')}
        </Button>
        <Button
          size={'LG'}
          color={canSave ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSave}
          onClick={save}
        >
          {isSending
            ? text('actions.saving')
            : text(isEdit ? 'actions.save' : 'actions.add')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default BillingExternalView;
